/**
 * SleepCueCards
 *
 * Shows tiredness cues organized by stage (early, middle, late).
 * Stages are revealed progressively based on current wake window status:
 * - Initially only early signals are visible
 * - Middle signals slide in from top when window_open
 * - Late signals slide in from top when window_closing/overtired
 * Final order (top to bottom): Late → Middle → Early
 */

import { useRef, useEffect } from 'react';
import { Eye, AlertCircle, AlertTriangle } from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import type { WakeWindowStatus } from '../lib/wakeWindowCalculator';
import type { SleepPlanMode } from '../lib/sleepScoring';

interface SleepCue {
  signal: string;
  stage: 'early' | 'middle' | 'late';
}

interface SleepCueCardsProps {
  cues: SleepCue[];
  currentStatus: WakeWindowStatus;
  /** Sleep plan mode – only show hint texts in active mode */
  mode?: SleepPlanMode;
}

/** Determine which stages should be visible based on wake window status */
function getVisibleStages(status: WakeWindowStatus): Set<'early' | 'middle' | 'late'> {
  switch (status) {
    case 'window_closing':
    case 'overtired':
      return new Set(['early', 'middle', 'late']);
    case 'window_open':
      return new Set(['early', 'middle']);
    case 'early_warning':
      return new Set(['early']);
    default:
      // awake/resting — show early by default (component is only rendered when relevant)
      return new Set(['early']);
  }
}

export function SleepCueCards({ cues, currentStatus, mode = 'active' }: SleepCueCardsProps) {
  const { t } = useTranslation('sleepPlan');
  const prevVisibleRef = useRef<Set<'early' | 'middle' | 'late'>>(new Set());

  // Group cues by stage
  const earlyCues = cues.filter(c => c.stage === 'early');
  const middleCues = cues.filter(c => c.stage === 'middle');
  const lateCues = cues.filter(c => c.stage === 'late');

  // Determine which stage is highlighted (active)
  const activeStage: 'early' | 'middle' | 'late' | null = (() => {
    switch (currentStatus) {
      case 'early_warning': return 'early';
      case 'window_open': return 'middle';
      case 'window_closing':
      case 'overtired': return 'late';
      default: return null;
    }
  })();

  const visibleStages = getVisibleStages(currentStatus);

  // Track which stages are newly revealed for animation
  const newlyRevealed = new Set<'early' | 'middle' | 'late'>();
  visibleStages.forEach(s => {
    if (!prevVisibleRef.current.has(s)) {
      newlyRevealed.add(s);
    }
  });

  useEffect(() => {
    prevVisibleRef.current = visibleStages;
  });

  // Order: late on top, middle in the middle, early at the bottom
  const stages = [
    {
      key: 'late' as const,
      icon: AlertTriangle,
      title: t('cues.late'),
      hint: t('cues.lateHint'),
      color: '#ef4444',
      cues: lateCues,
    },
    {
      key: 'middle' as const,
      icon: AlertCircle,
      title: t('cues.middle'),
      hint: t('cues.middleHint'),
      color: '#f97316',
      cues: middleCues,
    },
    {
      key: 'early' as const,
      icon: Eye,
      title: t('cues.early'),
      hint: t('cues.earlyHint'),
      color: '#eab308',
      cues: earlyCues,
    },
  ];

  return (
    <div className="space-y-3">
      <h3 className="text-white font-medium">{t('cues.title')}</h3>

      {stages.map(stage => {
        if (!visibleStages.has(stage.key)) return null;

        const isActive = activeStage === stage.key;
        const isNew = newlyRevealed.has(stage.key);

        return (
          <div
            key={stage.key}
            className={`rounded-[15px] p-4 transition-all duration-500 ease-out ${
              isActive
                ? 'glass-effect'
                : 'bg-white/10'
            } ${isNew ? 'animate-slide-down' : ''}`}
          >
            <div className="flex items-center gap-2 mb-2">
              <stage.icon
                className="w-4 h-4 text-white/75"
              />
              <span className="text-white text-sm font-medium">{stage.title}</span>
              {isActive && mode === 'active' && (
                <span
                  className="text-xs px-2 py-0.5 rounded-full ml-auto bg-pp-teal text-white"
                >
                  {stage.hint}
                </span>
              )}
            </div>

            <div className="flex flex-wrap gap-2">
              {stage.cues.map(cue => (
                <span
                  key={cue.signal}
                  className={`text-xs px-2 py-1 rounded-full ${
                    isActive
                      ? 'bg-white/15 text-white'
                      : 'bg-white/5 text-white/50'
                  }`}
                >
                  {t(`cues.signals.${cue.signal}` as never)}
                </span>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
